/**
 * A styled QR code, for the stickers a restaurant prints and puts on a table.
 *
 * The shared `QrCode` in `@yummy/ui-rnr` encodes a flat square grid with the
 * `qrcode` package. This is the merchant app's own variant: these codes are the
 * ones that get laminated and left on a table for months, so they carry rounded
 * modules and the Yummy mark in the middle.
 *
 * It lives here rather than in the UI package on purpose. `qr-code-styling`
 * draws through the DOM, and `@yummy/ui-rnr` is platform-split — the customer
 * app's native sibling must not inherit a browser-only dependency for a code
 * that a merchant scans off a phone screen. Styling a table sticker is this
 * app's problem alone.
 *
 * The encoded result is still handed to a real `<img>`, for the reason spelled
 * out in the shared component: React Native Web's `Image` paints its source as a
 * CSS background, browsers leave backgrounds out of a print, and a sticker whose
 * entire purpose is the code printed blank.
 */

import { useEffect, useState } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { View } from 'react-native';

import { Stack, Text } from '@yummy/ui-rnr';
import type { QrCodeProps } from '@yummy/ui-rnr';

import { YUMMY_MARK } from './yummy-mark';

/**
 * The sticker's palette, in descending order of how much the code leans on it.
 *
 * The data modules are near-black on white: that is the bulk of the symbol, and
 * the part a camera has to read at an angle, in poor light, through a scratched
 * laminate. `#1C1917` is the same ink the shared `QrCode` reaches for, and it
 * sits at 17.5:1 against the field.
 *
 * The finder patterns — the three big squares a scanner uses to find the code
 * at all — take the brand orange as a deliberate trade: 3.46:1 instead of
 * 17.5:1, in exchange for a sticker that looks like the restaurant's rather
 * than a shipping label's. That clears the ~3:1 most binarizers need and the
 * merchant app's own verification decodes it, but it is the first thing to give
 * up if a cheap printer renders orange faintly. `--primary-strong` (`#C03F0C`,
 * 5.31:1) is the darker drop-in if that ever turns up in the field.
 */
const QR_INK = '#1C1917';
const QR_FIELD = '#FFFFFF';
const QR_CORNER = '#E85E30';

/** Error correction levels, matching the shared component so the two cannot drift. */
type ErrorLevel = NonNullable<QrCodeProps['level']>;

export interface TableQrCodeProps {
  /** The signed table URL to encode. */
  value: string;
  /** Rendered edge length in density-independent pixels. */
  size?: number;
  /** Quiet zone in modules; the QR spec asks for at least 4. */
  margin?: number;
  /**
   * Error correction level.
   *
   * A code with the mark in the middle needs more recovery than a plain one:
   * covering the centre destroys real modules, and the level is what pays for
   * them. `Q` is the floor here — never `L`.
   */
  level?: ErrorLevel;
  /** Announced to assistive technology, and used as the image's alt text. */
  label?: string;
}

/**
 * Renders a table URL as a branded, printable QR code.
 *
 * @param props The value to encode, its size, and the table's label.
 * @returns The rendered code, or a readable placeholder while encoding.
 */
export function TableQrCode({
  value,
  size = 220,
  margin = 4,
  level = 'Q',
  label,
}: TableQrCodeProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const qr = new QRCodeStyling({
      type: 'svg',
      width: size,
      height: size,
      margin,
      data: value,
      qrOptions: { errorCorrectionLevel: level },
      // A machine-read symbol, so the palette's dark-mode values are the wrong
      // ones: the code is dark ink on white in either theme. See the shared
      // `QrCode` for the long version of why.
      dotsOptions: { type: 'rounded', color: QR_INK },
      cornersSquareOptions: { type: 'extra-rounded', color: QR_CORNER },
      cornersDotOptions: { type: 'dot', color: QR_CORNER },
      // Explicitly white rather than transparent: the quiet zone around the code
      // is part of the symbol, and a transparent one would pick up whatever the
      // sticker is printed on.
      backgroundOptions: { color: QR_FIELD },
      image: YUMMY_MARK,
      imageOptions: {
        // The mark's square is cleared of modules rather than painted over, so
        // the scanner is never asked to read orange through orange.
        hideBackgroundDots: true,
        // A quarter of the width, which clears under 6% of the modules — well
        // inside what `Q` can recover — and gives the tile enough room to read
        // as a badge on the printed sheet.
        imageSize: 0.24,
        // No inset. The tile is drawn to the edges of this square, so a margin
        // would undo the point of it: a ring of white between the orange and the
        // modules is the disc this replaced.
        margin: 0,
        // Inlines the mark as base64 instead of referencing it by URL. An SVG
        // shown through an `<img>` may not load external resources, so this is
        // what keeps the mark visible in the finished image.
        saveAsBlob: true,
      },
    });

    qr.getRawData('svg')
      .then(async (raw) => {
        // `getRawData` types as a Blob or a Node Buffer; only the browser case
        // can arrive here, but the union has to be narrowed before it can be
        // read as text.
        if (cancelled || !(raw instanceof Blob)) return;
        const svg = await raw.text();
        if (cancelled) return;
        setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (!cancelled) {
          setError(cause instanceof Error ? cause.message : 'Could not render QR code');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [value, size, margin, level]);

  // A visible failure beats a blank square: a merchant holding a sticker that
  // will not scan has no way to know whether the code or the printer is at
  // fault, and the URL at least lets them hand it over by hand.
  if (error) {
    return (
      <Stack gap="xs" align="center">
        <Text className="text-destructive text-center text-xs font-medium">{error}</Text>
        <Text selectable className="text-subtle-foreground text-center font-mono text-[11px]">
          {value}
        </Text>
      </Stack>
    );
  }

  return (
    <View
      className="items-center justify-center overflow-hidden rounded-lg"
      // Painted behind the code so the frame matches the encoder's white field.
      style={{ width: size, height: size, backgroundColor: QR_FIELD }}
      accessibilityRole="image"
      accessibilityLabel={label ? `QR code: ${label}` : 'QR code'}>
      {src ? (
        <img
          src={src}
          alt={label ? `QR code: ${label}` : 'QR code'}
          width={size}
          height={size}
          style={{ display: 'block' }}
        />
      ) : null}
    </View>
  );
}
